---
title: "Custom fields for candidates"
description: "Add custom fields to candidate records in Chosen. Define text, date, number, select, and other field types, auto-fill them from resumes, and filter on them."
category: "candidates"
keywords:
  - "custom fields ATS"
  - "candidate custom fields"
  - "custom candidate data"
  - "candidate field types"
schema: "HowTo"
nav:
  section: "guides"
  order: 40
  parent: "/guides/candidates"
---

Custom fields are columns you add to the candidate record beyond the built-in ones. Define a field once and it applies to every candidate in your organization — as a column in the [table](/guides/candidates/table-and-boards), a filter, and something [search](/guides/search) can match. Resume parsing can fill them in for you.

## What you can add

Each custom field has a type, which controls how its values are stored and displayed:

| Type | For |
| --- | --- |
| **Text** | Free-form short text. |
| **Email** | An email address. |
| **Phone** | A phone number. |
| **URL** | A link — portfolio, profile. |
| **Date** | A calendar date. |
| **Number** | A numeric value, like years of experience. |
| **Yes/No** | A simple boolean. |
| **Single-select** | One choice from a list of options you define. |

Pick the type that fits. A "Years of experience" field as a number sorts and filters properly; the same thing as text doesn't.

## Adding a field

<Steps>
  <li>From the candidate list, open the field manager (the settings control above the table).</li>
  <li>Under **Add New Field**, give it a name and choose a type. For a single-select, add the options.</li>
  <li>Optionally write a description — this is what the resume parser reads to know what to extract, so be specific ("Total years of professional engineering experience" beats "experience").</li>
  <li>Leave **Auto-fill from resume** on if you want parsing to populate it; turn it off for fields you'll only ever set by hand.</li>
  <li>Save. The field becomes a column on every candidate.</li>
</Steps>

You can edit or hide a field later from the same place. Built-in fields — Name, Email, Date Applied and the rest — can be shown or hidden but not edited or deleted; only fields your org added are fully editable. A few names belong to built-in columns (Date Applied, Stage, Links, Match Rating), so a custom field can't take them.

<Callout type="info" title="Made your own Date Applied field?">
The built-in [Date Applied](/guides/candidates/table-and-boards) column is already filled in for every candidate, from Chosen's own records. Turn it on, and delete your custom one once you've moved anything you typed into it.
</Callout>

## Auto-fill from resumes

A custom field with **Auto-fill from resume** on gets extracted during [resume parsing](/guides/candidates/resume-parsing), in the same pass as the built-in fields. The field's description is the instruction the parser follows — a vague description gets vague results, so it's worth a sentence.

Turn auto-fill off for anything a resume wouldn't contain — an internal rating, a referral source, a recruiter note. The parser then skips it and you fill it in yourself.

<Callout type="warning" title="A new field starts empty on existing candidates">
Adding an auto-fill field doesn't reach backward. Candidates parsed before the field existed won't have a value for it — the parser only fills fields that existed when it ran. To backfill an existing candidate, open their detail panel and **Reparse**; the field gets populated from their resume on the next run.
</Callout>

## Using fields in filters and search

Once a field exists, it's a column you can sort and a filter you can apply. It's also visible to [smart and deep search](/guides/search) — a single-select "Visa status" or a number "Years of experience" becomes something you can search against directly.

## Limits

Custom fields are organization-wide — there's no per-job field set, so a field added for one role shows up on every candidate. A single-select holds a fixed list of options; if candidates need to pick several values at once, that's not what single-select does. And the parser fills fields from resume text only — it can't invent a value the resume never stated.
